import type { routes } from '@semios/app-platform-banyan-route-definitions'
import type { TTooltipPlotLine } from 'App/dashboards/_utils/buildPestTooltip'
import type { SeriesOptionsType } from 'highcharts/highstock'
import { translate } from 'i18n/i18n'
import moment from 'moment-timezone'
import { dashboardColors } from 'settings/colors'

// Matches the magenta biofix marker on the existing insect trap/degree day charts.
const BIOFIX_COLOR = dashboardColors.biofix
const BIOFIX_ID_PREFIX = 'DASHBOARD_BIOFIX_PLOTLINE_'

/**
 * Vertical biofix markers from the cumulative degree day metadata (`metadata.biofixDates`, already
 * sorted + trimmed to the relevant range by the API). Degree days are points-scoped now, anchored to
 * the DEFAULT biofix (per insect + hemisphere), so every station reports the same biofix date(s); we
 * dedupe to one line per distinct day. Present only when degree days were requested/permitted (caller
 * gates the fetch via the ddKey). User-defined biofixes are intentionally not shown here (deferred).
 */
export const buildBiofixPlotLines = ({
  data,
  stationLngLats,
  ddKey,
  timezone,
}: {
  data: routes.Values.Response | null
  stationLngLats: string[]
  ddKey: string
  timezone: string
}): { plotLines: TTooltipPlotLine[]; legendSeries: SeriesOptionsType | null } => {
  const points = data?.points as
    Record<string, { values?: Record<string, { metadata?: { biofixDates?: string[] } }[]> }> | undefined

  const biofixDayMsSet = new Set<number>()

  stationLngLats.forEach((lngLat) => {
    const biofixDates = points?.[lngLat]?.values?.[ddKey]?.[0]?.metadata?.biofixDates ?? []

    biofixDates.forEach((biofixDate) => {
      biofixDayMsSet.add(+moment.tz(biofixDate, timezone).startOf('day'))
    })
  })

  if (biofixDayMsSet.size === 0) return { plotLines: [], legendSeries: null }

  const plotLines: TTooltipPlotLine[] = Array.from(biofixDayMsSet).map((dayMs) => {
    return {
      id: `${BIOFIX_ID_PREFIX}${dayMs}`,
      value: dayMs,
      color: BIOFIX_COLOR,
      width: 2,
      dashStyle: 'ShortDot',
      zIndex: 3,
      label: {
        text: translate.phrases.banyanApp('Biofix'),
        rotation: 90,
        align: 'left',
        verticalAlign: 'top',
        style: { color: BIOFIX_COLOR, fontSize: '11px' },
      },
      tooltipDisplay: () => translate.phrases.banyanApp('Biofix'),
    }
  })

  // Empty line series purely to give "Biofix" a legend entry; clicking it toggles the plot lines.
  const legendSeries: SeriesOptionsType = {
    type: 'line',
    id: 'dashboard-biofix',
    name: translate.phrases.banyanApp('Biofix'),
    color: BIOFIX_COLOR,
    dashStyle: 'ShortDot',
    data: [],
    marker: { enabled: false },
    events: {
      legendItemClick: function () {
        const xAxis = this.chart.xAxis?.[0]

        if (!xAxis) return

        const willHide = this.visible

        if (willHide) {
          plotLines.forEach((pl) => pl.id && xAxis.removePlotLine(pl.id))
        } else {
          plotLines.forEach((pl) => xAxis.addPlotLine(pl))
        }
      },
    },
  }

  return { plotLines, legendSeries }
}
